import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Slide from '@material-ui/core/Slide';
import makeStyles from '@material-ui/core/styles/makeStyles';
import React, { ComponentType, forwardRef, memo, MemoExoticComponent, useCallback } from 'react';
import clsx from 'clsx';
import AlertGlobalProvider, { showGlobalAlert } from './global';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Divider from '@material-ui/core/Divider';

interface IProps {
  opened: boolean;
  message?: React.ReactNode;
  title?: string;
  mode?: string;
  confirmation?: boolean;
  global?: boolean;
  onClose: (ok: boolean) => void;
}

export interface IAlertShowParams {
  message?: React.ReactNode;
  mode?: string;
  title?: string;
  confirmation?: boolean;
}

type AlertComponent = MemoExoticComponent<ComponentType<IProps>> & {
  Global?: typeof AlertGlobalProvider;

  show?(params: string): Promise<boolean>;
  show?(params: IAlertShowParams): Promise<boolean>;

  confirm?(params: string): Promise<boolean>;
  confirm?(params: IAlertShowParams): Promise<boolean>;
};

const useStyle = makeStyles(theme => ({
  root: {
    zIndex: '1600 !important' as any
  },
  content: {
    minWidth: '250px'
  },
  deleteTitle: {
    color: '#E24C4B'
  },
  deleteButton: {
    color: '#FFF',
    background: '#E24C4B',
    '&:hover': {
      background: darken('#E24C4B', 0.2)
    }
  }
}));

const Alert: AlertComponent = memo((props: IProps) => {
  const { message, title, onClose, confirmation, opened, mode } = props;
  const classes = useStyle(props);
  const isDelete = mode === 'delete';
  const handleOk = useCallback(() => onClose(true), [onClose]);
  const handleCancel = useCallback(() => onClose(false), [onClose]);

  return (
    <Dialog
      open={opened}
      keepMounted
      TransitionComponent={Transition}
      onClose={handleCancel}
      className={classes.root}
      maxWidth={isDelete ? 'xs' : 'sm'}
      fullWidth={isDelete}
    >
      <DialogTitle className={clsx({ [classes.deleteTitle]: isDelete })}>
        <strong>{title || (confirmation ? 'Confirmação' : 'Atenção')}</strong>
      </DialogTitle>
      {message && (
        <DialogContent>
          <DialogContentText className={classes.content}>{message}</DialogContentText>
        </DialogContent>
      )}
      {!message && (
        <Divider />
      )}
      <DialogActions>
        {confirmation && (
          <Button onClick={handleCancel} autoFocus>
            Cancelar
          </Button>
        )}
        <Button
          autoFocus={!confirmation}
          onClick={handleOk}
          color="primary"
          className={clsx({ [classes.deleteButton]: isDelete })}
        >
          {isDelete ? 'Sim, confirmar exclusão' : 'OK'}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

Alert.Global = AlertGlobalProvider;

Alert.show = (params: string | IAlertShowParams) => {
  const paramsData = typeof params === 'string' ? { message: params } : params;
  return showGlobalAlert(paramsData);
};

Alert.confirm = (params: string | IAlertShowParams) => {
  const paramsData = typeof params === 'string' ? { message: params } : params;
  return showGlobalAlert({ ...paramsData, confirmation: true });
};

const Transition = memo(
  forwardRef((props: any, ref: any) => {
    return <Slide direction='up' {...props} ref={ref} />;
  })
);

export default Alert;
